/**
 * Statuses that mark a moment in the real world, so it's worth asking when that
 * moment actually was — a technician arriving on site at 09:05 may only log it
 * at 11:02. Callouts use Arrived/LeftSite; sessions use Started/Ended.
 *
 * Everything else records an office decision (Booked, Review, ReadyToInvoice,
 * Cancelled…), where the event *is* the act of recording it, so a separate
 * event time would be noise. Booked is the near-miss: a phone booking taken at
 * 09:00 and captured at 11:00 did "happen" earlier, but the time that matters
 * for a booking — when the visit is scheduled — is already the service's own
 * start/end time, and a second date on the audit entry would just compete with
 * it. Add a status here if that trade-off changes.
 */
const EVENT_TIME_STATUSES = new Set<string>([
  'Arrived',
  'LeftSite',
  'Started',
  'Ended',
])

/** Whether a move to `status` should offer a "when did this happen?" time. */
export function statusRecordsAnEventTime(status: string): boolean {
  return EVENT_TIME_STATUSES.has(status)
}

/**
 * Statuses that mean someone has started working the service — a job going
 * InProgress, a session Started, a callout Arrived on site. Moving here
 * auto-assigns the service to the person doing it, if no one is assigned yet.
 */
const WORK_STARTED_STATUSES = new Set<string>([
  'InProgress',
  'Started',
  'Arrived',
])

/** Whether moving to `status` means work was picked up (see auto-assign). */
export function statusStartsWork(status: string): boolean {
  return WORK_STARTED_STATUSES.has(status)
}

/**
 * Statuses that stall the work while it waits on something outside the shop.
 *
 * Blocked is the catch-all; the rest name a specific thing being waited on, so
 * the office can see and filter what kind of stall it is without reading the
 * reason text. They are peers, not a hierarchy — a job is either Blocked or
 * WaitingOnParts, never both — and they behave identically: red, reason
 * required, reachable from anywhere, returning to any working status.
 *
 * This set is what makes a new specific reason cheap. Blocked used to be
 * special-cased by string literal in ten places; adding a sibling meant finding
 * all ten. Add the status here (plus a label, a colour and a migration) and the
 * family checks pick it up.
 */
const BLOCKING_STATUSES = new Set<string>(['Blocked', 'WaitingOnParts'])

/** Whether `status` means the work is stalled waiting on something. */
export function statusIsBlocking(status: string): boolean {
  return BLOCKING_STATUSES.has(status)
}

/**
 * Statuses nobody should be able to move to silently.
 *
 * The blocking statuses and the unsuccessful collection all stall or end the
 * work, and Cancelled closes the record for good — in each case the next person
 * to open it needs to know why, and the reason is the only place that survives.
 * This lives here rather than at each dialog because the rule was previously
 * re-spelled at three call sites, and every one of them had quietly forgotten
 * Cancelled.
 *
 * A specific blocking status narrows *what* is being waited on; it does not
 * excuse saying which parts, or which customer, or how long.
 */
const REASON_REQUIRED_STATUSES = new Set<string>([
  ...BLOCKING_STATUSES,
  'Cancelled',
  'ReadyForCollectionUnsuccessful',
])

/** Whether moving to `status` demands an explanation. */
export function statusRequiresReason(status: string): boolean {
  return REASON_REQUIRED_STATUSES.has(status)
}

/** Statuses whose confirmation should read as destructive rather than routine. */
export function statusIsDestructive(status: string): boolean {
  return status === 'Cancelled'
}

/**
 * Universal status-transition rule: any status may move to any other status of
 * its service type, in the type's canonical order. Two footnotes:
 *
 *  - The initial placeholder (New/NewJob) is never a *target* — records leave
 *    it, they don't return to it.
 *  - The current status stays offered (except from the initial one) so its
 *    reason text can be updated — recorded as a fresh audit entry.
 *
 * This replaced a per-type transition map layered with special cases; the maps
 * still document each type's nominal flow (and feed the Help pages) but no
 * longer restrict anything. What keeps free movement safe is everything around
 * it, which all still applies: reason-required statuses, the audit trail in
 * `*_status_changes`, and the field lock on Invoiced/Cancelled records.
 */
export function withUniversalTransitions<S extends string>(
  _current: S,
  _base: readonly S[],
  opts: { order: readonly S[]; initial: S },
): S[] {
  return opts.order.filter((status) => status !== opts.initial)
}

export interface StatusReasonPrompt {
  /** Placeholder for the reason field. */
  placeholder: string
  /** Warning line under the field, shown when a reason is required. */
  warning: string
}

const GENERIC_REQUIRED_PROMPT: StatusReasonPrompt = {
  placeholder: 'Required. Why is this status changing?',
  warning: 'Add a short reason so the office team knows why this is moving.',
}

/**
 * What to ask for when moving to `status`. This lives in the domain because the
 * dialog used to spell it as a ternary chain keyed on status literals, so every
 * new status meant another arm — and a forgotten arm silently fell through to
 * generic copy that asked for the wrong thing.
 */
const REASON_PROMPTS: Record<string, StatusReasonPrompt> = {
  Blocked: {
    placeholder: 'What are you waiting on? e.g. parts, customer approval…',
    warning:
      'Blocked services need a reason so the office knows what they’re waiting on.',
  },
  WaitingOnParts: {
    placeholder:
      'Which parts are you waiting on? e.g. 2x DDR4 8GB SODIMM, ordered 29 Jul',
    warning:
      'List the parts and when they were ordered so the office can chase the supplier.',
  },
  Cancelled: {
    placeholder: 'Required. Why is this being cancelled? e.g. customer withdrew…',
    warning:
      'Cancelling closes the record. The reason is kept with it and cannot be added later without reopening.',
  },
  ReadyForCollectionUnsuccessful: {
    placeholder: 'Why couldn’t the repair be completed?',
    warning: 'Add a short reason so the office team knows why this is moving.',
  },
}

/** Reason-field copy for a status; generic wording when it has none of its own. */
export function statusReasonPrompt(status: string): StatusReasonPrompt {
  return REASON_PROMPTS[status] ?? GENERIC_REQUIRED_PROMPT
}
